import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import TemplateEditModal from '../components/TemplateEditModal';
import { 
  Search, 
  Video, 
  Image as ImageIcon, 
  Music, 
  Package,
  Clock,
  Star,
  Filter,
  ChevronRight,
  Sparkles,
  Bookmark,
  TrendingUp,
  Target,
  GraduationCap,
  Smile,
  ShoppingBag,
  Heart,
  Zap,
  Play,
  AlertCircle,
  Eye,
  Edit3,
  Trash2,
  Plus,
  FolderOpen,
  User
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Icons for content types
const contentTypeIcons = {
  video: Video,
  image: ImageIcon,
  music: Music,
  package: Package
};

// Icons for use cases
const useCaseIcons = {
  social_media: TrendingUp,
  marketing: Target,
  education: GraduationCap,
  entertainment: Smile,
  ecommerce: ShoppingBag,
  lifestyle: Heart
};

// Colors for content types
const contentTypeColors = {
  video: 'bg-primary/20 text-primary border-primary/30',
  image: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  music: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  package: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
};

// Difficulty badges
const difficultyColors = {
  beginner: 'bg-green-500/20 text-green-400',
  intermediate: 'bg-yellow-500/20 text-yellow-400',
  advanced: 'bg-red-500/20 text-red-400'
};

const TemplatesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('system');
  const [templates, setTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [categories, setCategories] = useState({ content_types: [], use_cases: [] });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});
  const [promptPreview, setPromptPreview] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchUserTemplates();
    fetchCategories();
  }, [selectedContentType, selectedUseCase]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedContentType) params.append('content_type', selectedContentType);
      if (selectedUseCase) params.append('use_case', selectedUseCase);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/api/templates/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTemplates = async () => {
    try {
      const params = new URLSearchParams();
      params.append('include_user', 'true');
      if (selectedContentType) params.append('content_type', selectedContentType);
      if (selectedUseCase) params.append('use_case', selectedUseCase);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/api/templates/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter to only user templates (not system)
      const allTemplates = response.data.templates || [];
      const userOnly = allTemplates.filter(t => !t.is_system && t.user_id);
      setUserTemplates(userOnly);
    } catch (error) {
      console.error('Failed to fetch user templates:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/templates/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTemplates();
    fetchUserTemplates();
  };

  const openTemplateModal = (template) => {
    setSelectedTemplate(template);
    // Initialize variable values with defaults
    const defaults = {};
    (template.variables || []).forEach(v => {
      defaults[v.name] = v.default || '';
    });
    setVariableValues(defaults);
    setValidationErrors({});
    setValidationWarnings({});
    setPromptPreview('');
  };

  const openEditModal = (template) => {
    setTemplateToEdit(template);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    fetchUserTemplates();
  };

  const handleDeleteSuccess = () => {
    fetchUserTemplates();
  };

  const closeTemplateModal = () => {
    setSelectedTemplate(null);
    setVariableValues({});
    setValidationErrors({});
    setValidationWarnings({});
    setPromptPreview('');
  };

  const handleVariableChange = (name, value) => {
    setVariableValues(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate variables client-side
  const validateVariables = () => {
    if (!selectedTemplate) return true;
    
    const errors = {};
    (selectedTemplate.variables || []).forEach(v => {
      if (v.required !== false) { // Default to required
        const value = variableValues[v.name] || '';
        if (!value.trim()) {
          errors[v.name] = `${v.label || v.name} is required`;
        }
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Generate prompt preview
  const generatePreview = async () => {
    if (!selectedTemplate) return;
    
    setIsValidating(true);
    try {
      const response = await axios.post(`${API}/api/templates/${selectedTemplate.id}/validate`, {
        template_id: selectedTemplate.id,
        variables: variableValues
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPromptPreview(response.data.filled_prompt || '');
      setValidationErrors(response.data.errors || {});
      setValidationWarnings(response.data.warnings || {});
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Check if form has required fields filled
  const hasRequiredFieldsFilled = () => {
    if (!selectedTemplate) return false;
    
    for (const v of (selectedTemplate.variables || [])) {
      if (v.required !== false) { // Default to required
        const value = variableValues[v.name] || '';
        if (!value.trim()) return false;
      }
    }
    return true;
  };

  const useTemplate = async () => {
    if (!selectedTemplate) return;
    
    // Client-side validation first
    if (!validateVariables()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/templates/${selectedTemplate.id}/use`, {
        template_id: selectedTemplate.id,
        variables: variableValues
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { prompt, settings, content_type, package_items } = response.data;
      
      // Navigate to appropriate studio with pre-filled data
      const studioRoutes = {
        video: '/studio/videos',
        image: '/studio/images',
        music: '/studio/music',
        package: '/studio/combine'
      };
      
      // Store template data in sessionStorage for the studio to pick up
      sessionStorage.setItem('template_data', JSON.stringify({
        prompt,
        settings,
        content_type,
        package_items,
        template_name: selectedTemplate.name
      }));
      
      toast.success(`Template "${selectedTemplate.name}" loaded!`);
      navigate(studioRoutes[content_type] || '/videos');
      
    } catch (error) {
      console.error('Failed to use template:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 400 && error.response?.data?.detail?.errors) {
        const backendErrors = error.response.data.detail.errors;
        setValidationErrors(backendErrors);
        toast.error('Please fill in all required fields');
      } else {
        toast.error('Failed to apply template');
      }
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `~${seconds}s`;
    const mins = Math.floor(seconds / 60);
    return `~${mins} min`;
  };

  // Template Card Component
  const TemplateCard = ({ template }) => {
    const TypeIcon = contentTypeIcons[template.content_type] || Sparkles;
    const UseCaseIcon = useCaseIcons[template.use_case] || Target;
    
    return (
      <div 
        className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
        onClick={() => openTemplateModal(template)}
        data-testid={`template-card-${template.id}`}
      >
        {/* Thumbnail */}
        {template.thumbnail_url ? (
          <div className="relative h-32 w-full overflow-hidden bg-muted">
            <img 
              src={template.thumbnail_url} 
              alt={template.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-full h-full items-center justify-center bg-muted">
              <TypeIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            {/* Type badge overlay */}
            <div className={`absolute top-2 left-2 p-1.5 rounded-md ${contentTypeColors[template.content_type]}`}>
              <TypeIcon className="w-4 h-4" />
            </div>
            {template.is_featured && (
              <div className="absolute top-2 right-2 flex items-center gap-1 text-xs bg-yellow-500/90 text-black px-2 py-1 rounded-md font-medium">
                <Star className="w-3 h-3 fill-current" />
                Featured
              </div>
            )}
          </div>
        ) : (
          <div className="h-32 w-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
            <div className={`p-4 rounded-xl ${contentTypeColors[template.content_type]}`}>
              <TypeIcon className="w-8 h-8" />
            </div>
            {template.is_featured && (
              <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-yellow-500">
                <Star className="w-3.5 h-3.5 fill-current" />
                Featured
              </div>
            )}
          </div>
        )}
        
        {/* Content */}
        <div className="p-4">
          {/* Title & Description */}
          <h3 className="font-semibold text-base mb-1.5 group-hover:text-primary transition-colors line-clamp-1">
            {template.name}
          </h3>
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {template.description}
          </p>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyColors[template.difficulty]}`}>
              {template.difficulty}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground flex items-center gap-1">
              <UseCaseIcon className="w-3 h-3" />
              {template.use_case?.replace('_', ' ')}
            </span>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              {formatTime(template.estimated_time)}
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-primary hover:text-primary hover:bg-primary/10 gap-1"
            >
              Use Template
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // User Template Card Component (with edit/delete actions)
  const UserTemplateCard = ({ template }) => {
    const TypeIcon = contentTypeIcons[template.content_type] || Sparkles;
    const UseCaseIcon = useCaseIcons[template.use_case] || Target;
    
    return (
      <div 
        className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all"
        data-testid={`user-template-card-${template.id}`}
      >
        {/* Thumbnail or Placeholder */}
        {template.thumbnail_url ? (
          <div className="relative h-28 w-full overflow-hidden bg-muted">
            <img 
              src={template.thumbnail_url} 
              alt={template.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-full h-full items-center justify-center bg-muted">
              <TypeIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            {/* Type badge overlay */}
            <div className={`absolute top-2 left-2 p-1.5 rounded-md ${contentTypeColors[template.content_type]}`}>
              <TypeIcon className="w-4 h-4" />
            </div>
            {/* Edit button overlay */}
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                openEditModal(template);
              }}
              className="absolute top-2 right-2 h-7 w-7 p-0 bg-black/50 hover:bg-black/70 text-white"
              data-testid={`edit-template-${template.id}`}
            >
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <div className="relative h-28 w-full bg-gradient-to-br from-blue-500/10 to-blue-600/5 flex items-center justify-center">
            <div className={`p-3 rounded-xl ${contentTypeColors[template.content_type]}`}>
              <TypeIcon className="w-6 h-6" />
            </div>
            {/* Edit button overlay */}
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                openEditModal(template);
              }}
              className="absolute top-2 right-2 h-7 w-7 p-0 bg-black/30 hover:bg-black/50 text-white"
              data-testid={`edit-template-${template.id}`}
            >
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
        
        {/* Content */}
        <div className="p-4">
          {/* Title & Description */}
          <h3 
            className="font-semibold text-base mb-1.5 group-hover:text-primary transition-colors cursor-pointer line-clamp-1"
            onClick={() => openTemplateModal(template)}
          >
            {template.name}
          </h3>
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {template.description || 'No description'}
          </p>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 flex items-center gap-1">
              <User className="w-3 h-3" />
              My Template
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground flex items-center gap-1">
              <UseCaseIcon className="w-3 h-3" />
              {template.use_case?.replace('_', ' ')}
            </span>
            {(template.variables || []).length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                {template.variables.length} field{template.variables.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              {template.created_at ? new Date(template.created_at).toLocaleDateString() : 'Unknown'}
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-primary hover:text-primary hover:bg-primary/10 gap-1"
              onClick={() => openTemplateModal(template)}
            >
              Use Template
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Template Modal
  const TemplateModal = () => {
    if (!selectedTemplate) return null;
    
    const TypeIcon = contentTypeIcons[selectedTemplate.content_type] || Sparkles;
    
    return (
      <div 
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
        onClick={closeTemplateModal}
      >
        <div 
          className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl ${contentTypeColors[selectedTemplate.content_type]}`}>
                <TypeIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-1">{selectedTemplate.name}</h2>
                <p className="text-muted-foreground">{selectedTemplate.description}</p>
              </div>
            </div>
            
            {/* Meta info */}
            <div className="flex items-center gap-4 mt-4 text-sm">
              <span className={`px-2 py-1 rounded-full ${difficultyColors[selectedTemplate.difficulty]}`}>
                {selectedTemplate.difficulty}
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="w-4 h-4" />
                {formatTime(selectedTemplate.estimated_time)}
              </span>
              {selectedTemplate.use_count > 0 && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Play className="w-4 h-4" />
                  {selectedTemplate.use_count} uses
                </span>
              )}
            </div>
          </div>
          
          {/* Modal Body - Variables */}
          <div className="p-6 max-h-[50vh] overflow-y-auto">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              Customize Your Content
            </h3>
            
            {selectedTemplate.variables?.length > 0 ? (
              <div className="space-y-4">
                {selectedTemplate.variables.map((variable) => {
                  const hasError = validationErrors[variable.name];
                  const hasWarning = validationWarnings[variable.name];
                  
                  return (
                    <div key={variable.name}>
                      <label className="block text-sm font-medium mb-1.5">
                        {variable.label}
                        {variable.required !== false && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">{variable.description}</p>
                      
                      {variable.type === 'select' ? (
                        <select
                          value={variableValues[variable.name] || ''}
                          onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                          className={`w-full p-2.5 bg-background border rounded-lg text-sm focus:border-primary outline-none transition-colors ${
                            hasError ? 'border-red-500 focus:border-red-500' : 'border-border'
                          }`}
                          data-testid={`var-${variable.name}`}
                        >
                          {variable.options?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : variable.type === 'textarea' ? (
                        <textarea
                          value={variableValues[variable.name] || ''}
                          onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                          placeholder={variable.placeholder}
                          rows={3}
                          className={`w-full p-2.5 bg-background border rounded-lg text-sm focus:border-primary outline-none resize-none transition-colors ${
                            hasError ? 'border-red-500 focus:border-red-500' : 'border-border'
                          }`}
                          data-testid={`var-${variable.name}`}
                        />
                      ) : (
                        <Input
                          type={variable.type === 'number' ? 'number' : 'text'}
                          value={variableValues[variable.name] || ''}
                          onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                          placeholder={variable.placeholder}
                          className={`bg-background transition-colors ${
                            hasError ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                          data-testid={`var-${variable.name}`}
                        />
                      )}
                      
                      {/* Error message */}
                      {hasError && (
                        <p className="flex items-center gap-1 text-xs text-red-500 mt-1">
                          <AlertCircle className="w-3 h-3" />
                          {validationErrors[variable.name]}
                        </p>
                      )}
                      
                      {/* Warning message */}
                      {hasWarning && !hasError && (
                        <p className="flex items-center gap-1 text-xs text-yellow-500 mt-1">
                          <AlertCircle className="w-3 h-3" />
                          {validationWarnings[variable.name]}
                        </p>
                      )}
                    </div>
                  );
                })}
                
                {/* Prompt Preview */}
                {promptPreview && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
                      <Eye className="w-4 h-4 text-primary" />
                      Prompt Preview
                    </h4>
                    <p className="text-sm bg-muted/50 p-3 rounded-lg text-muted-foreground whitespace-pre-wrap">
                      {promptPreview}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                This template has no customizable variables. Click "Use Template" to proceed.
              </p>
            )}
            
            {/* Package Items Preview */}
            {selectedTemplate.content_type === 'package' && selectedTemplate.package_items?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="font-medium mb-3">Package Includes:</h4>
                <div className="grid gap-2">
                  {selectedTemplate.package_items.map((item, idx) => {
                    const ItemIcon = contentTypeIcons[item.type] || Sparkles;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <ItemIcon className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          {/* Modal Footer */}
          <div className="p-6 border-t border-border flex items-center justify-between">
            <Button variant="ghost" onClick={closeTemplateModal}>
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              {/* Preview Button - only show if template has variables */}
              {selectedTemplate.variables?.length > 0 && (
                <Button 
                  variant="outline"
                  onClick={generatePreview}
                  disabled={isValidating}
                  className="gap-2"
                  data-testid="preview-template-btn"
                >
                  <Eye className="w-4 h-4" />
                  {isValidating ? 'Validating...' : 'Preview'}
                </Button>
              )}
              
              <Button 
                onClick={useTemplate}
                className="gap-2"
                disabled={selectedTemplate.variables?.length > 0 && !hasRequiredFieldsFilled()}
                data-testid="use-template-btn"
              >
                <Sparkles className="w-4 h-4" />
                Use Template
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Templates</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2">
            Templates Library
          </h1>
          <p className="text-zinc-500 text-sm">
            Start creating faster with pre-built templates.
          </p>
        </div>
        
        {/* Search & Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card"
                data-testid="template-search"
              />
            </div>
          </form>
          
          {/* Content Type Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0">
            <Button
              variant={selectedContentType === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedContentType(null)}
              className="whitespace-nowrap"
            >
              All Types
            </Button>
            {categories.content_types?.map(ct => {
              const Icon = contentTypeIcons[ct.id] || Sparkles;
              return (
                <Button
                  key={ct.id}
                  variant={selectedContentType === ct.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedContentType(ct.id)}
                  className="whitespace-nowrap gap-1.5"
                  data-testid={`filter-${ct.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {ct.name}
                  <span className="text-xs opacity-70">({ct.count})</span>
                </Button>
              );
            })}
          </div>
        </div>
        
        {/* Use Case Pills */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Filter className="w-4 h-4" />
            Use Case:
          </span>
          <Button
            variant={selectedUseCase === null ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedUseCase(null)}
            className="whitespace-nowrap"
          >
            All
          </Button>
          {categories.use_cases?.map(uc => {
            const Icon = useCaseIcons[uc.id] || Target;
            return (
              <Button
                key={uc.id}
                variant={selectedUseCase === uc.id ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setSelectedUseCase(uc.id)}
                className="whitespace-nowrap gap-1"
              >
                <Icon className="w-3.5 h-3.5" />
                {uc.name}
              </Button>
            );
          })}
        </div>
        
        {/* Tabs for System / My Templates */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="system" className="gap-2" data-testid="system-templates-tab">
              <Sparkles className="w-4 h-4" />
              System Templates
              <span className="ml-1 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">
                {templates.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="my" className="gap-2" data-testid="my-templates-tab">
              <Bookmark className="w-4 h-4" />
              My Templates
              <span className="ml-1 text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full">
                {userTemplates.length}
              </span>
            </TabsTrigger>
          </TabsList>
          
          {/* System Templates Tab */}
          <TabsContent value="system" className="mt-6">
            {/* Templates Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
                    <div className="w-12 h-12 bg-muted rounded-lg mb-4" />
                    <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-full mb-2" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-16">
                <Sparkles className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No templates found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your filters or search query
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map(template => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            )}
            
            {/* Stats */}
            {!loading && templates.length > 0 && (
              <div className="mt-8 text-center text-sm text-muted-foreground">
                Showing {templates.length} system template{templates.length !== 1 ? 's' : ''}
              </div>
            )}
          </TabsContent>
          
          {/* My Templates Tab */}
          <TabsContent value="my" className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
                    <div className="w-12 h-12 bg-muted rounded-lg mb-4" />
                    <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-full mb-2" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : userTemplates.length === 0 ? (
              <div className="text-center py-16">
                <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No templates yet</h3>
                <p className="text-muted-foreground mb-6">
                  You haven't saved any templates yet. Create content in any studio and click "Save as Template" to get started.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button onClick={() => navigate('/studio/videos')} className="gap-2">
                    <Video className="w-4 h-4" />
                    Video Studio
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/studio/images')} className="gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Image Studio
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/studio/music')} className="gap-2">
                    <Music className="w-4 h-4" />
                    Music Studio
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {userTemplates.map(template => (
                  <UserTemplateCard key={template.id} template={template} />
                ))}
              </div>
            )}
            
            {/* Stats */}
            {!loading && userTemplates.length > 0 && (
              <div className="mt-8 text-center text-sm text-muted-foreground">
                Showing {userTemplates.length} of your template{userTemplates.length !== 1 ? 's' : ''}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Template Use Modal */}
      <TemplateModal />
      
      {/* Template Edit Modal */}
      <TemplateEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        template={templateToEdit}
        onSaveSuccess={handleEditSuccess}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </div>
    </AppShell>
  );
};

export default TemplatesPage;
